import { useState, useCallback, createContext, useContext } from 'react';
import { AlertTriangle, Trash2, Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ConfirmContext = createContext(null);

const VARIANTS = {
  warning: { icon: AlertTriangle, iconBg: 'bg-amber-100', iconColor: 'text-amber-600', confirmClass: 'bg-amber-600 hover:bg-amber-700' },
  danger: { icon: Trash2, iconBg: 'bg-red-100', iconColor: 'text-red-600', confirmClass: 'bg-red-600 hover:bg-red-700' },
  info: { icon: Info, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', confirmClass: 'bg-blue-600 hover:bg-blue-700' },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback(({ title, message, confirmText = 'Fortsett', cancelText = 'Avbryt', variant = 'warning', extraActions = [] }) => {
    return new Promise((resolve) => {
      setState({ title, message, confirmText, cancelText, variant, extraActions, resolve });
    });
  }, []);

  const handleConfirm = () => { state?.resolve(true); setState(null); };
  const handleCancel = () => { state?.resolve(false); setState(null); };
  const handleExtra = (value) => { state?.resolve(value); setState(null); };

  const v = state ? (VARIANTS[state.variant] || VARIANTS.warning) : VARIANTS.warning;
  const Icon = v.icon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={handleCancel}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 ${v.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                  <Icon className={`w-5 h-5 ${v.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-slate-900 mb-1">{state.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{state.message}</p>
                </div>
                <button onClick={handleCancel} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0 -mt-1 -mr-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 bg-slate-50 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={handleCancel}>{state.cancelText}</Button>
              {(state.extraActions || []).map((action, idx) => (
                <Button key={idx} variant={action.variant || 'outline'} size="sm" onClick={() => handleExtra(action.value)}>{action.label}</Button>
              ))}
              <Button size="sm" className={v.confirmClass} onClick={handleConfirm}>{state.confirmText}</Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
